import type { StateValueBudget } from './StateValue';

/** Aggregate checkpoint metadata is larger than a tile state, but shares the
 * project-wide budget. It uses ordinary JSON plus the codec's undefined/-0. */
export function validateContinuationValue(
  root: unknown,
  budget: StateValueBudget = { members: 0, text: 0 },
): void {
  const ancestors = new Set<object>();
  let members = 0,
    text = 0;
  const chargeText = (value: string) => {
    text += value.length;
    budget.text += value.length;
    if (text > 8 * 1024 * 1024 || budget.text > 16 * 1024 * 1024)
      throw new RangeError('Continuation text exceeds limit');
  };
  const visit = (value: unknown, depth: number): void => {
    if (++members > 500_000 || ++budget.members > 2_000_000 || depth > 64)
      throw new RangeError('Continuation data exceeds limit');
    if (value === null || value === undefined || typeof value === 'boolean') return;
    if (typeof value === 'string') return chargeText(value);
    if (typeof value === 'number' && Number.isFinite(value)) return;
    if (typeof value !== 'object') throw new TypeError('Unsupported continuation value');
    const array = Array.isArray(value),
      prototype = Object.getPrototypeOf(value);
    if (
      array
        ? prototype !== Array.prototype
        : prototype !== Object.prototype && prototype !== null
    )
      throw new TypeError('Continuation must contain plain data');
    if (ancestors.has(value)) throw new TypeError('Circular continuation data');
    if (
      array &&
      (members + value.length > 500_000 || budget.members + value.length > 2_000_000)
    )
      throw new RangeError('Continuation data exceeds limit');
    ancestors.add(value);
    try {
      for (const key of Reflect.ownKeys(value)) {
        const property = Object.getOwnPropertyDescriptor(value, key)!;
        if (typeof key !== 'string' || !('value' in property))
          throw new TypeError('Unsupported continuation property');
        if (array && key === 'length') continue;
        if (array && (!/^(0|[1-9]\d*)$/.test(key) || Number(key) >= value.length))
          throw new TypeError('Unsupported continuation array property');
        if (!property.enumerable)
          throw new TypeError('Unsupported hidden continuation property');
        chargeText(key);
        visit(property.value, depth + 1);
      }
      if (array && Object.keys(value).length !== value.length)
        throw new TypeError('Sparse continuation arrays are unsupported');
    } finally {
      ancestors.delete(value);
    }
  };
  visit(root, 0);
}
